const VOID_TAGS = ['br'];


function normalizeAllowedTags(tags = ['br']) {
    return tags.map(tag => String(tag).replace(/[<>/]/g, '').toLowerCase());
}


function parseHtmlString(str) {
    const $root = document.createElement('div');
    $root.innerHTML = String(str).trim();
    return $root;
}


function escapeHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}


function isWordBreakChar(ch) {
    return ch === ' ' || ch === '\n' || ch === '\t' || ch === '\r';
}


function serializeSrOnly($root, allowedTags) {
    let html = '';

    function walk($node) {
        $node.childNodes.forEach($child => {
            if ( $child.nodeType === Node.TEXT_NODE ) {
                html += escapeHtml($child.textContent);
                return;
            }

            if ( $child.nodeType !== Node.ELEMENT_NODE ) return;

            const tag = $child.tagName.toLowerCase();

            if ( allowedTags.includes(tag) ) {
                if ( VOID_TAGS.includes(tag) ) {
                    html += `<${tag}>`;
                } else {
                    html += `<${tag}>`;
                    walk($child);
                    html += `</${tag}>`;
                }
                return;
            }

            walk($child);
        });
    }

    walk($root);
    return html;
}


function walkHtmlString($root, allowedTags, handlers) {
    function walk($node) {
        $node.childNodes.forEach($child => {
            if ( $child.nodeType === Node.TEXT_NODE ) {
                handlers.onText($child.textContent);
                return;
            }

            if ( $child.nodeType !== Node.ELEMENT_NODE ) return;

            const tag = $child.tagName.toLowerCase();

            if ( allowedTags.includes(tag) ) {
                if ( VOID_TAGS.includes(tag) ) {
                    handlers.onVoid(tag);
                } else {
                    handlers.onTagOpen(tag);
                    walk($child);
                    handlers.onTagClose(tag);
                }
                return;
            }

            walk($child);
        });
    }

    walk($root);
}


function buildSplitMarkup(givenStr, options, wrapChar) {
    const allowedTags = normalizeAllowedTags(options.allowedTags);
    const $root = parseHtmlString(givenStr);

    let html = '';
    let wordIsOpen = false;

    function openWord() {
        if ( wordIsOpen ) return;
        html += options.wordOpen;
        wordIsOpen = true;
    }

    function closeWord() {
        if ( !wordIsOpen ) return;
        html += options.wordClose;
        wordIsOpen = false;
    }

    walkHtmlString($root, allowedTags, {
        onText(text) {
            for ( const ch of text ) {
                if ( isWordBreakChar(ch) ) {
                    closeWord();
                    html += ' ';
                } else {
                    openWord();
                    html += wrapChar(ch);
                }
            }
        },
        onVoid(tag) {
            closeWord();
            html += `<${tag}>`;
        },
        onTagOpen(tag) {
            html += `<${tag}>`;
        },
        onTagClose(tag) {
            html += `</${tag}>`;
        }
    });

    closeWord();

    return `<span class="sr-only">${serializeSrOnly($root, allowedTags)}</span>` + html;
}


// ===========================================================
// Characters 
// ===========================================================

export function getStringToCharacters(givenStr = null, options = {}) {
    if ( !givenStr ) return;

    const defaultOptions = {
        wordInner: false,
        charInner: false,
        allowedTags: ['br'],
    };
    options = { ...defaultOptions, ...options };

    const wordOpen = options.wordInner
        ? '<span class="word" aria-hidden="true"><span class="word-inner">'
        : '<span class="word" aria-hidden="true">';
    const wordClose = options.wordInner ? '</span></span>' : '</span>';

    return buildSplitMarkup(givenStr, {
        ...options,
        wordOpen,
        wordClose
    }, (ch) => {
        const safe = escapeHtml(ch);
        return options.charInner
            ? `<span class="char"><span class="char-inner">${safe}</span></span>`
            : `<span class="char">${safe}</span>`;
    });
}


const CHAR_TIMEOUTS_KEY = '__charTimeouts';

function clearCharAnimationTimeouts($parent) {
    const timeouts = $parent?.[CHAR_TIMEOUTS_KEY];

    if (!timeouts?.length) return;

    timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    $parent[CHAR_TIMEOUTS_KEY] = [];
}

function storeCharAnimationTimeout($parent, timeoutId) {
    if (!$parent) return;

    if (!$parent[CHAR_TIMEOUTS_KEY]) {
        $parent[CHAR_TIMEOUTS_KEY] = [];
    }

    $parent[CHAR_TIMEOUTS_KEY].push(timeoutId);
}


function calcDurationInterval($parent, duration) {
    if (!$parent || !duration) return 1000;

    const $chars = $parent.querySelectorAll('.char');
    const charCount = $chars?.length || 0;

    return duration / charCount;
}


export function stringCharactersAddActive($parent = null, options = {interval: null, duration: null}) {
    if (!$parent) return;

    let interval = options.interval ?? 10;

    if ( options.duration ) {
        interval = calcDurationInterval($parent, options.duration);
    }

    clearCharAnimationTimeouts($parent);

    const $chars = $parent.querySelectorAll('.char');
    let charTimeout = 0;

    $chars?.forEach($char => {
        charTimeout = charTimeout + interval;

        const timeoutId = setTimeout(function(){
            $char.classList.add('active');
        }, charTimeout);

        storeCharAnimationTimeout($parent, timeoutId);
    });
}


export function stringCharactersRemoveActive($parent = null, options = {interval: null, duration: null}) {
    if (!$parent) return;

    let interval = options.interval ?? 10;

    if ( options.duration ) {
        interval = calcDurationInterval($parent, options.duration);
    }

    clearCharAnimationTimeouts($parent);

    const $chars = $parent.querySelectorAll('.char');
    let charTimeout = 0 - interval;

    $chars?.forEach($char => {
        charTimeout = charTimeout + interval;

        const timeoutId = setTimeout(function(){
            $char.classList.remove('active');
        }, charTimeout);

        storeCharAnimationTimeout($parent, timeoutId);
    });
}


export function stringCharactersRemoveActiveReverse($parent = null, options = {interval: null, duration: null}) {
    if (!$parent) return;

    let interval = options.interval ?? 10;

    if ( options.duration ) {
        interval = calcDurationInterval($parent, options.duration);
    }

    clearCharAnimationTimeouts($parent);

    const $chars = Array.from($parent.querySelectorAll('.char')).reverse();
    let charTimeout = 0 - interval;

    $chars?.forEach($char => {
        charTimeout = charTimeout + interval;

        const timeoutId = setTimeout(function(){
            $char.classList.remove('active');
        }, charTimeout);

        storeCharAnimationTimeout($parent, timeoutId);
    });
}


// ===========================================================
// Words
// ===========================================================

export function getStringToWords(givenStr = null, options = {}) {
    if ( !givenStr ) return;

    const defaultOptions = {
        allowedTags: ['br'],
    };
    options = { ...defaultOptions, ...options };

    return buildSplitMarkup(givenStr, {
        ...options,
        wordOpen: '<span class="word" aria-hidden="true">',
        wordClose: '</span>'
    }, (ch) => escapeHtml(ch)).trim();
}

const WORD_TIMEOUTS_KEY = '__wordTimeouts';

function clearWordAnimationTimeouts($parent) {
    const timeouts = $parent?.[WORD_TIMEOUTS_KEY];

    if (!timeouts?.length) return;

    timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    $parent[WORD_TIMEOUTS_KEY] = [];
}

function storeWordAnimationTimeout($parent, timeoutId) {
    if (!$parent) return;

    if (!$parent[WORD_TIMEOUTS_KEY]) {
        $parent[WORD_TIMEOUTS_KEY] = [];
    }

    $parent[WORD_TIMEOUTS_KEY].push(timeoutId);
}


function calcWordDurationInterval($parent, duration) {
    if (!$parent || !duration) return 1000;

    const $words = $parent.querySelectorAll('.word');
    const wordCount = $words?.length || 0;

    return duration / wordCount;
}


export function stringWordsAddActive($parent = null, options = {interval: null, duration: null}) {
    if (!$parent) return;

    let interval = options.interval ?? 10;

    if ( options.duration ) {
        interval = calcWordDurationInterval($parent, options.duration);
    }

    clearWordAnimationTimeouts($parent);

    const $words = $parent.querySelectorAll('.word');
    let wordTimeout = 0;

    $words?.forEach($word => {
        wordTimeout = wordTimeout + interval;

        const timeoutId = setTimeout(function(){
            $word.classList.add('active');
        }, wordTimeout);

        storeWordAnimationTimeout($parent, timeoutId);
    });
}


export function stringWordsRemoveActive($parent = null, options = {interval: null, duration: null}) {
    if (!$parent) return;

    let interval = options.interval ?? 10;

    if ( options.duration ) {
        interval = calcWordDurationInterval($parent, options.duration);
    }

    clearWordAnimationTimeouts($parent);

    const $words = $parent.querySelectorAll('.word');
    let wordTimeout = 0 - interval;

    $words?.forEach($word => {
        wordTimeout = wordTimeout + interval;

        const timeoutId = setTimeout(function(){
            $word.classList.remove('active');
        }, wordTimeout);

        storeWordAnimationTimeout($parent, timeoutId);
    });
}


export function stringWordsRemoveActiveReverse($parent = null, options = {interval: null, duration: null}) {
    if (!$parent) return;

    let interval = options.interval ?? 10;

    if ( options.duration ) {
        interval = calcWordDurationInterval($parent, options.duration);
    }

    clearWordAnimationTimeouts($parent);

    const $words = Array.from($parent.querySelectorAll('.word')).reverse();
    let wordTimeout = 0 - interval;

    $words?.forEach($word => {
        wordTimeout = wordTimeout + interval;

        const timeoutId = setTimeout(function(){
            $word.classList.remove('active');
        }, wordTimeout);

        storeWordAnimationTimeout($parent, timeoutId);
    });
}